import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LANGUAGES, adventuresFor } from './adventures';

/**
 * NavScreen — standalone navigation for the Speaking Tutor.
 *
 * Shown when the app is opened bare (no ?adventure and no WordPress link
 * params — see main.jsx). Two steps: choose your language → choose your
 * adventure. Selecting a playable adventure does a full-page navigation to
 * ?lang=<id>&adventure=<id>, which remounts the game cleanly (the engine
 * reads its content pack from the URL at module load).
 *
 * Locked tiles show the roadmap: every Little Sponges language and all 15
 * curriculum adventures are on the map even though only English + Farm +
 * Playground are live — deliberate, for the investor/demo story.
 */

const BRAND = {
  gold: '#F7AD00',
  teal: '#05544B',
  tealMid: '#0A7B6B',
  tealBright: '#0CD3BC',
  white: '#FFFFFF',
};

function goTo(lang, adventure) {
  const q = new URLSearchParams(window.location.search);
  q.set('lang', lang);
  q.set('adventure', adventure);
  window.location.search = q.toString();
}

function Sky() {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
      background: 'linear-gradient(180deg, #7EC8E3 0%, #ADE4F7 55%, #D4F1FF 100%)' }}>
      {/* soft grass band at the bottom, matching the game's drawn scene */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '22vh',
        background: 'linear-gradient(180deg, #6DBE45 0%, #4A9E26 100%)',
        borderTopLeftRadius: '50% 30%', borderTopRightRadius: '50% 30%' }} />
    </div>
  );
}

function Tile({ locked, badge, onClick, children, delay = 0, wide = false }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.25 }}
      whileHover={locked ? {} : { scale: 1.06 }}
      whileTap={locked ? {} : { scale: 0.97 }}
      onClick={locked ? undefined : onClick}
      style={{
        position: 'relative',
        width: wide ? 200 : 168,
        minHeight: 128,
        borderRadius: 22,
        border: locked ? '3px solid rgba(5,84,75,0.15)' : `3px solid ${BRAND.teal}`,
        background: locked ? 'rgba(255,255,255,0.55)' : BRAND.white,
        boxShadow: locked ? 'none' : '0 6px 18px rgba(5,84,75,0.25)',
        cursor: locked ? 'default' : 'pointer',
        padding: '14px 10px 12px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
        fontFamily: "'Nunito', sans-serif",
        filter: locked ? 'grayscale(55%)' : 'none',
        opacity: locked ? 0.75 : 1,
      }}
    >
      {children}
      {badge && (
        <span style={{ position: 'absolute', top: -12, right: -8, background: badge.color,
          color: BRAND.white, borderRadius: 999, fontSize: 12, fontWeight: 900,
          padding: '4px 10px', boxShadow: '0 2px 6px rgba(0,0,0,0.25)', whiteSpace: 'nowrap' }}>
          {badge.text}
        </span>
      )}
    </motion.button>
  );
}

export default function NavScreen() {
  const [lang, setLang] = useState(null); // null → language step
  const step = lang ? 'adventure' : 'language';

  return (
    <div style={{ minHeight: '100vh', position: 'relative', fontFamily: "'Nunito', sans-serif",
      display: 'flex', flexDirection: 'column', alignItems: 'center', overflowX: 'hidden' }}>
      <Sky />
      <div style={{ position: 'fixed', top: 14, left: 20, zIndex: 10 }}>
        <img src="/images/little-sponges-logo.png" alt="Little Sponges®"
          style={{ height: 40, objectFit: 'contain', userSelect: 'none' }} />
      </div>

      <div style={{ zIndex: 1, width: '100%', maxWidth: 1100, padding: '76px 24px 60px',
        display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

        <motion.h1 initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          style={{ color: BRAND.teal, fontSize: 44, fontWeight: 900, margin: '0 0 4px', textAlign: 'center' }}>
          Speaking Tutor
        </motion.h1>

        <AnimatePresence mode="wait">
          {step === 'language' ? (
            <motion.div key="language" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <p style={{ color: BRAND.tealMid, fontSize: 24, fontWeight: 800, margin: '4px 0 28px' }}>
                Choose your language
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, justifyContent: 'center', maxWidth: 840 }}>
                {LANGUAGES.map((l, i) => {
                  const locked = l.status !== 'ready';
                  return (
                    <Tile key={l.id} locked={locked} delay={i * 0.05} wide
                      badge={locked ? { text: 'Coming soon', color: BRAND.gold } : null}
                      onClick={() => setLang(l.id)}>
                      <span style={{ fontSize: 44, lineHeight: 1 }}>{l.flag}</span>
                      <span style={{ fontSize: 21, fontWeight: 900, color: BRAND.teal }}>{l.name}</span>
                      {l.native !== l.name && (
                        <span style={{ fontSize: 15, fontWeight: 700, color: BRAND.tealMid }}>{l.native}</span>
                      )}
                      {locked && <span style={{ fontSize: 15 }}>🔒</span>}
                    </Tile>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            <motion.div key="adventure" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <p style={{ color: BRAND.tealMid, fontSize: 24, fontWeight: 800, margin: '4px 0 8px' }}>
                Choose your adventure
              </p>
              <button onClick={() => setLang(null)}
                style={{ background: 'none', border: 'none', color: BRAND.tealMid, fontWeight: 800,
                  fontSize: 16, cursor: 'pointer', marginBottom: 20, fontFamily: "'Nunito', sans-serif" }}>
                ← {LANGUAGES.find((l) => l.id === lang)?.flag} {LANGUAGES.find((l) => l.id === lang)?.name} — change language
              </button>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, justifyContent: 'center' }}>
                {adventuresFor(lang).map((a, i) => {
                  const locked = a.status === 'soon';
                  const badge = a.status === 'ready' ? null
                    : a.status === 'building' ? { text: 'In the works', color: BRAND.tealBright }
                    : { text: 'Coming soon', color: BRAND.gold };
                  return (
                    <Tile key={a.id} locked={locked} delay={i * 0.04}
                      badge={badge}
                      onClick={() => a.status === 'ready' && goTo(lang, a.id)}>
                      <span style={{ fontSize: 46, lineHeight: 1 }}>{a.emoji}</span>
                      <span style={{ fontSize: 18, fontWeight: 900, color: BRAND.teal, textAlign: 'center' }}>{a.title}</span>
                      {locked ? <span style={{ fontSize: 14 }}>🔒</span>
                        : a.status === 'ready' && (
                          <span style={{ fontSize: 13, fontWeight: 900, color: BRAND.white, background: BRAND.tealMid,
                            borderRadius: 999, padding: '3px 12px' }}>Play!</span>
                        )}
                    </Tile>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
